import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, Field, Modal, QueryStatus, ScaledInput, useToast } from "@cx/ui";
import type { CommissionRuleView } from "@cx/contracts";
import { formatPct, toPct4 } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ProductSearch } from "../prices/ProductSearch";
import { bandText, ruleTargetText } from "./labels";

/** Número do percentual sem o símbolo, para o campo com sufixo "%". */
const pctNumber = (pct4: number) => formatPct(pct4).replace("%", "");

type RuleValues = {
  productId: string | null;
  productName: string | null;
  discountFromPct4: number | null;
  discountToPct4: number | null;
  commissionPct4: number | null;
  priority: number;
};
const EMPTY_RULE: RuleValues = {
  productId: null,
  productName: null,
  discountFromPct4: null,
  discountToPct4: null,
  commissionPct4: null,
  priority: 0,
};

/**
 * Regras de comissão de um vendedor (`sellerId`) ou as gerais, para todos os vendedores (sem `sellerId`). Precedência
 * (05 §6): vendedor + produto > vendedor + faixa > produto (geral) > padrão do vendedor.
 */
export function CommissionRulesBox({ sellerId }: { sellerId?: string }): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const query = sellerId ? `sellerId=${sellerId}` : "scope=general";
  const key = ["commission-rules", companyId, query];
  const rules = useQuery({
    queryKey: key,
    queryFn: () => api<{ data: CommissionRuleView[] }>(`/commission-rules?${query}`),
  });
  const [editing, setEditing] = useState<{ id?: string; values: RuleValues } | null>(null);
  const [deleting, setDeleting] = useState<CommissionRuleView | null>(null);
  const changed = () => {
    qc.invalidateQueries({ queryKey: ["commission-rules", companyId] });
    // A simulação da comissão (chave ["sellers", empresa, id, "commission", …]) depende das regras.
    qc.invalidateQueries({ queryKey: ["sellers", companyId] });
  };
  const del = useMutation({
    mutationFn: (id: string) => api(`/commission-rules/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Regra de comissão excluída");
      changed();
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const title = sellerId ? "Regras de comissão do vendedor" : "Regras para todos os vendedores";
  return (
    <div className="box card p-3 mb-3">
      <div className="d-flex align-items-center mb-2">
        <h2 className="h6 mb-0 flex-grow-1">{title}</h2>
        {can(perms, "sellers", "create") && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => setEditing({ values: EMPTY_RULE })}
          >
            Incluir regra
          </button>
        )}
      </div>
      <p className="form-text mt-0">
        {sellerId
          ? "Por produto ou por faixa de desconto concedido. Valem antes das regras gerais e da comissão padrão."
          : "Por produto, para qualquer vendedor. Valem quando o vendedor não tem regra própria."}
      </p>
      <QueryStatus loading={rules.isLoading} error={rules.error} />
      {rules.isSuccess && rules.data.data.length === 0 && (
        <p className="text-muted mb-0">Nenhuma regra cadastrada.</p>
      )}
      {rules.isSuccess && rules.data.data.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0" aria-label={title}>
            <thead>
              <tr>
                <th scope="col">Produto</th>
                <th scope="col">Desconto concedido</th>
                <th scope="col" className="text-end">
                  Comissão
                </th>
                <th scope="col" className="text-end">
                  Prioridade
                </th>
                <th scope="col">
                  <span className="visually-hidden">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rules.data.data.map((r) => (
                <tr key={r.id}>
                  <td>{ruleTargetText(r)}</td>
                  <td>{bandText(r.discountFromPct4, r.discountToPct4)}</td>
                  <td className="text-end">{formatPct(r.commissionPct4)}</td>
                  <td className="text-end">{r.priority}</td>
                  <td className="text-end text-nowrap">
                    {can(perms, "sellers", "update") && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-2"
                        aria-label={`Editar regra ${ruleTargetText(r)}`}
                        onClick={() =>
                          setEditing({
                            id: r.id,
                            values: {
                              productId: r.productId,
                              productName: r.productName,
                              discountFromPct4: r.discountFromPct4,
                              discountToPct4: r.discountToPct4,
                              commissionPct4: r.commissionPct4,
                              priority: r.priority,
                            },
                          })
                        }
                      >
                        Editar
                      </button>
                    )}
                    {can(perms, "sellers", "delete") && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        aria-label={`Excluir regra ${ruleTargetText(r)}`}
                        disabled={del.isPending}
                        onClick={() => setDeleting(r)}
                      >
                        Excluir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <RuleDialog
          sellerId={sellerId ?? null}
          id={editing.id}
          initial={editing.values}
          onClose={() => setEditing(null)}
          onSaved={changed}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Excluir regra de comissão"
        message="Excluir esta regra? Ela pode ser restaurada pela Consulta de exclusões."
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleting) del.mutate(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function RuleDialog({
  sellerId,
  id,
  initial,
  onClose,
  onSaved,
}: {
  sellerId: string | null;
  id?: string;
  initial: RuleValues;
  onClose(): void;
  onSaved(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [v, setV] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof RuleValues>(k: K, value: RuleValues[K]) => setV((x) => ({ ...x, [k]: value }));
  const save = useMutation({
    mutationFn: () =>
      api(id ? `/commission-rules/${id}` : "/commission-rules", {
        method: id ? "PUT" : "POST",
        body: JSON.stringify({
          sellerId,
          productId: v.productId,
          discountFromPct4: v.discountFromPct4,
          discountToPct4: v.discountToPct4,
          commissionPct4: v.commissionPct4,
          priority: v.priority,
        }),
      }),
    onSuccess: () => {
      toast("success", "Regra de comissão salva");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_commission_rule")
        setErrors({ productId: e.message });
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title={id ? "Editar regra de comissão" : "Nova regra de comissão"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div
        onKeyDown={(e) => {
          // Enter num campo do diálogo não submete o formulário da página de trás.
          if (e.key === "Enter" && e.target instanceof HTMLInputElement) e.preventDefault();
        }}
      >
        <div className="mb-2" data-testid="rule-product">
          <span className="form-label d-block">Produto</span>
          {v.productId ? (
            <span>
              <strong>{v.productName}</strong>{" "}
              <button
                type="button"
                className="btn btn-sm btn-link"
                onClick={() => setV((x) => ({ ...x, productId: null, productName: null }))}
              >
                Trocar
              </button>
            </span>
          ) : (
            <span className="text-muted">
              {sellerId ? "Todos os produtos (regra por faixa de desconto)" : "Escolha um produto"}
            </span>
          )}
          {errors.productId && <div className="invalid-feedback d-block">{errors.productId}</div>}
        </div>
        {!v.productId && (
          <ProductSearch
            id="rule-product-search"
            label="Buscar produto"
            action="Escolher"
            onPick={(p) => setV((x) => ({ ...x, productId: p.id, productName: p.name }))}
          />
        )}
        <div className="row">
          <Field label="Desconto de" className="col-md-6" error={errors.discountFromPct4} hint="Vazio = 0%.">
            <ScaledInput
              value={v.discountFromPct4}
              parse={toPct4}
              format={pctNumber}
              suffix="%"
              onChange={(p) => set("discountFromPct4", p)}
            />
          </Field>
          <Field
            label="Desconto até"
            className="col-md-6"
            error={errors.discountToPct4}
            hint="Vazio = sem limite."
          >
            <ScaledInput
              value={v.discountToPct4}
              parse={toPct4}
              format={pctNumber}
              suffix="%"
              onChange={(p) => set("discountToPct4", p)}
            />
          </Field>
          <Field label="Comissão" required className="col-md-6" error={errors.commissionPct4}>
            <ScaledInput
              value={v.commissionPct4}
              parse={toPct4}
              format={pctNumber}
              suffix="%"
              onChange={(p) => set("commissionPct4", p)}
            />
          </Field>
          <Field
            label="Prioridade"
            className="col-md-6"
            error={errors.priority}
            hint="Desempata regras do mesmo nível: maior vence."
          >
            <input
              type="number"
              step={1}
              value={v.priority}
              onChange={(e) => set("priority", Math.trunc(Number(e.target.value) || 0))}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
